import { isProvisional } from "@rally/rating";
import { demoMemberById, demoRatings } from "./demo.js";
import { SPORT } from "./sport.js";
import { supabase } from "./supabase.js";
import type { Kind, MemberCategory } from "./types.js";

export interface LeaderboardRow {
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
  r: number;
  rd: number;
  matchesPlayed: number;
  delta?: number;
  provisional: boolean;
  category: MemberCategory | null;
}

/**
 * One board per discipline, optionally narrowed to a category.
 *
 * The sport is fixed by the deployment; kind and category are not. Filtering by category
 * ranks people against their own -- it never changes a rating. Mixed is not a third board
 * for the same reason it is not a third rating: a mixed match is recorded and does not
 * count (20260810000100), so there is nothing to rank.
 */
export async function fetchLeaderboard(
  kind: Kind,
  category?: MemberCategory,
): Promise<LeaderboardRow[]> {
  // The filter applies here too. Sample mode is what someone sees with no backend, and a
  // selector that visibly does nothing reads as a broken app rather than as sample data.
  if (!supabase) {
    const rows = sampleBoard(kind);
    return category ? rows.filter((row) => row.category === category) : rows;
  }

  let query = supabase
    .from("leaderboard")
    .select("member_id, display_name, avatar_url, r, rd, matches_played, provisional, category")
    .eq("sport", SPORT)
    .eq("kind", kind);

  // A member with no category set appears on the combined board only. Showing them on both
  // would be a guess, and showing them on neither would hide them from everything.
  if (category) query = query.eq("category", category);

  const { data, error } = await query.order("r", { ascending: false });

  if (error) throw new Error(error.message);

  return (data ?? []).map((row) => ({
    memberId: row.member_id,
    displayName: row.display_name,
    avatarUrl: row.avatar_url,
    r: row.r,
    rd: row.rd,
    matchesPlayed: row.matches_played,
    provisional: row.provisional,
    category: row.category,
  }));
}

/**
 * Sample data for local development before a Supabase project exists. Ratings are real
 * engine values so the lockup renders exactly as it will in production.
 *
 * Built from the same sample members and ratings every other screen reads, because the
 * board is now a way in to a profile. Its own private five players made every row lead to
 * somebody who did not exist.
 */
function sampleBoard(kind: Kind): LeaderboardRow[] {
  return demoRatings
    .filter((rating) => rating.kind === kind)
    .flatMap((rating) => {
      const member = demoMemberById(rating.memberId);
      if (!member || member.status !== "active") return [];

      return [
        {
          memberId: rating.memberId,
          displayName: member.displayName,
          avatarUrl: member.avatarUrl,
          r: rating.r,
          rd: rating.rd,
          matchesPlayed: rating.matchesPlayed,
          delta: rating.delta,
          provisional: isProvisional(rating.rd),
          category: member.category,
        },
      ];
    })
    .sort((a, b) => b.r - a.r);
}
